/**
 * ApprovalsInbox — Feb 2026 R138g
 *
 * A cross-journey inbox for owners, reviewers, and approvers. Each row
 * is one task the signed-in user has a stake in, showing:
 *   - The employee whose journey the task belongs to
 *   - The task title, due date, description
 *   - The caller's role(s) on the task (owner / reviewer / approver)
 *   - Per-role "sign-off" buttons (green tick when done)
 *   - Full progress timeline with who did what + when so everyone sees
 *     the audit trail.
 *
 * Route: /app/my/approvals — accessible to any signed-in user (the
 * endpoint scopes results to tasks they actually own / review / approve).
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Circle, ShieldCheck, Eye, Sparkles, LogOut, AlertTriangle, User, Inbox, ListChecks } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import TaskApprovalsTab from "@/components/tasks/TaskApprovalsTab";

const ROLE_LABEL = { owner: "Owner", reviewer: "Reviewer", approver: "Approver" };
const ROLE_ICON = { owner: User, reviewer: Eye, approver: ShieldCheck };

function StagePill({ role, done, at, byName }) {
  const Icon = ROLE_ICON[role];
  return (
    <span
      className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] ${done ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-muted/40 text-muted-foreground border-border"}`}
      title={done ? `${ROLE_LABEL[role]} · ${byName || "signed"} · ${(at || "").slice(0, 16).replace("T", " ")}` : `${ROLE_LABEL[role]} · pending`}
    >
      <Icon className="w-2.5 h-2.5" />
      {ROLE_LABEL[role]}
      {done ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />}
      {done && at && <span className="opacity-70 font-mono">·{at.slice(5, 10)}</span>}
    </span>
  );
}

function ApprovalRow({ row, membersById, onChanged }) {
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const overdue = row.due_date && row.due_date < today && !row.all_role_done;
  const nameFor = (uid) => membersById[uid]?.user_name || membersById[uid]?.name || membersById[uid]?.email || (uid ? uid.slice(0, 6) : null);

  const stages = [];
  stages.push({
    role: "owner",
    done: row.status === "done",
    at: row.completed_at,
    byName: nameFor(row.completed_by || row.assignee_user_id),
  });
  if (row.reviewer_user_id) {
    stages.push({
      role: "reviewer",
      done: !!row.reviewer_signed_at,
      at: row.reviewer_signed_at,
      byName: nameFor(row.reviewer_signed_by || row.reviewer_user_id),
    });
  }
  if (row.approver_user_id) {
    stages.push({
      role: "approver",
      done: !!row.approver_signed_at,
      at: row.approver_signed_at,
      byName: nameFor(row.approver_signed_by || row.approver_user_id),
    });
  }

  const act = async (roleKey) => {
    const done = row.role_done?.[roleKey];
    setBusy(true);
    try {
      await api.post(`/me/onboarding/approvals/${row.id}`, { role: roleKey, action: done ? "revoke" : "sign" });
      toast.success(done ? `${ROLE_LABEL[roleKey]} sign-off revoked` : `${ROLE_LABEL[roleKey]} · signed off`);
      await onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Action failed")); }
    finally { setBusy(false); }
  };

  const Kind = row.journey_kind === "offboarding" ? LogOut : Sparkles;

  return (
    <li
      className={`grid grid-cols-[200px_minmax(240px,1.6fr)_100px_120px_minmax(260px,1.4fr)_180px] gap-3 px-3 py-2.5 border-b border-border last:border-b-0 items-start text-sm ${overdue ? "bg-rose-500/[0.04]" : ""}`}
      data-testid={`approval-row-${row.id}`}
    >
      {/* Kind (employee is now shown in the group header) */}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Kind className="w-3 h-3" /> {row.journey_kind}</div>
        {row.category && <div className="text-[10px] text-muted-foreground/70 capitalize truncate">{String(row.category).replace(/_/g, " ")}</div>}
      </div>
      {/* Task */}
      <div className="min-w-0">
        <div className="text-sm font-medium flex items-center gap-1.5">
          {row.title}
          {overdue && <span className="text-[9px] uppercase tracking-widest bg-rose-500/10 text-rose-700 px-1 border border-rose-500/30 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" />overdue</span>}
          {row.is_required && <span className="text-[9px] uppercase tracking-widest bg-amber-500/10 text-amber-700 px-1 border border-amber-500/30">required</span>}
        </div>
        {row.description && <div className="text-[11px] text-muted-foreground line-clamp-2">{row.description}</div>}
      </div>
      {/* Due */}
      <div className={`text-[11px] font-mono ${overdue ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
        {row.due_date ? String(row.due_date).slice(0, 10) : "—"}
      </div>
      {/* My roles */}
      <div className="flex flex-wrap gap-1">
        {row.my_roles.map((r) => (
          <span key={r} className="chip text-[9px] uppercase tracking-widest px-1 border border-primary/40 bg-primary/5 text-primary" data-testid={`approval-role-${row.id}-${r}`}>{r}</span>
        ))}
      </div>
      {/* Progress timeline */}
      <div className="flex flex-wrap items-center gap-1">
        {stages.map((s, i) => (
          <span key={s.role} className="inline-flex items-center gap-0.5">
            {i > 0 && <span className={s.done || stages[i - 1].done ? "text-emerald-600" : "text-muted-foreground/40"}>→</span>}
            <StagePill role={s.role} done={s.done} at={s.at} byName={s.byName} />
          </span>
        ))}
      </div>
      {/* Actions — one per my role */}
      <div className="flex flex-col gap-1">
        {row.my_roles.map((r) => (
          <Button
            key={r}
            size="sm"
            className={`rounded-none h-7 text-xs ${row.role_done?.[r] ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
            variant={row.role_done?.[r] ? "default" : "outline"}
            onClick={() => act(r)}
            disabled={busy}
            data-testid={`approval-action-${row.id}-${r}`}
          >
            {row.role_done?.[r] ? <><CheckCircle2 className="w-3 h-3 mr-1" /> {ROLE_LABEL[r]} done · undo</> : <>Sign off as {ROLE_LABEL[r]}</>}
          </Button>
        ))}
      </div>
    </li>
  );
}

export default function ApprovalsInbox() {
  // R268mmm · Feb 2026 — Scope the Approvals Inbox to the currently
  // active workspace. Previously the endpoint spanned every workspace
  // the user belonged to and the members lookup fell back to
  // `rows[0].workspace_id`, so a stale first row could silently pull
  // members from an unrelated workspace and mix cross-workspace rows
  // into the visible list.
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [kind, setKind] = useState("all");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("pending");
  const [members, setMembers] = useState([]);
  // Iter Feb2026 · Outer tab — switch between the existing
  // onboarding/offboarding queue and the new Task-approval workflow.
  const [outerTab, setOuterTab] = useState("onboarding");

  const load = async () => {
    if (!wsId) { setRows([]); setBusy(false); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (kind !== "all") params.set("kind", kind);
      if (role !== "all") params.set("role", role);
      if (status) params.set("status", status);
      params.set("workspace_id", wsId);
      const r = await api.get(`/me/onboarding/approvals?${params.toString()}`);
      setRows(r.data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load approvals")); }
    finally { setBusy(false); }
  };

  // Load workspace members once (or whenever the active workspace
  // changes) so completed_by uids can be labelled.
  useEffect(() => {
    if (!wsId) { setMembers([]); return; }
    api.get(`/workspaces/${wsId}/members`)
      .then((m) => setMembers(m.data || []))
      .catch(() => setMembers([]));
  }, [wsId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, role, status, wsId]);

  const membersById = useMemo(() => Object.fromEntries((members || []).map((m) => [m.user_id || m.id, m])), [members]);
  const pendingCount = rows.filter((r) => !r.all_role_done).length;

  // Feb 2026 R138h — group by employee so approvers see all tasks for one
  // person together. Sort employees by name; within each group keep the
  // backend's pending-first / due-date ordering.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const empId = r.employee?.id || "_none_";
      if (!map.has(empId)) map.set(empId, { emp: r.employee || {}, rows: [] });
      map.get(empId).rows.push(r);
    }
    return Array.from(map.values()).sort((a, b) => (a.emp.full_name || "").localeCompare(b.emp.full_name || ""));
  }, [rows]);

  return (
    <div className="min-h-screen">
      <PageHeader
        icon={Inbox}
        eyebrow="ONBOARDING · APPROVALS"
        title="Waiting on you"
        description="Every onboarding and offboarding step across the workspace where you're on the hook — mark your side as done and everyone else sees the progress instantly."
      />
      <div className="px-4 sm:px-6 md:px-10 py-4 space-y-3">
        {/* Iter Feb2026 · Outer switch — Onboarding vs Tasks approval. */}
        <Tabs value={outerTab} onValueChange={setOuterTab}>
          <TabsList className="rounded-none">
            <TabsTrigger value="onboarding" className="rounded-none" data-testid="approvals-outer-onboarding">
              <Sparkles className="w-3 h-3 mr-1" />Onboarding &amp; HR
            </TabsTrigger>
            <TabsTrigger value="tasks" className="rounded-none" data-testid="approvals-outer-tasks">
              <ListChecks className="w-3 h-3 mr-1" />Tasks (budget gate)
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {outerTab === "tasks" && <TaskApprovalsTab />}
        {outerTab === "onboarding" && (<>
        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-2 border border-border p-2 bg-muted/20">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList className="rounded-none">
              <TabsTrigger value="all" className="rounded-none" data-testid="approvals-kind-all">All</TabsTrigger>
              <TabsTrigger value="onboarding" className="rounded-none" data-testid="approvals-kind-onboarding"><Sparkles className="w-3 h-3 mr-1" />Onboarding</TabsTrigger>
              <TabsTrigger value="offboarding" className="rounded-none" data-testid="approvals-kind-offboarding"><LogOut className="w-3 h-3 mr-1" />Offboarding</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="w-px h-6 bg-border mx-1" />
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground pl-1">Role</label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="rounded-none h-8 text-xs w-36" data-testid="approvals-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              <SelectItem value="owner">Owner (me)</SelectItem>
              <SelectItem value="reviewer">Reviewer (me)</SelectItem>
              <SelectItem value="approver">Approver (me)</SelectItem>
            </SelectContent>
          </Select>
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground pl-2">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none h-8 text-xs w-32" data-testid="approvals-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending on me</SelectItem>
              <SelectItem value="done">Fully signed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <div className="ml-auto text-[11px] text-muted-foreground pr-1" data-testid="approvals-count">
            {busy ? "Loading…" : (
              <><b className="text-foreground">{rows.length}</b> shown · <b className="text-rose-600">{pendingCount}</b> pending on you</>
            )}
          </div>
        </div>

        {/* Rows grouped by employee */}
        {busy && (
          <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="approvals-loading">Loading approvals…</div>
        )}
        {!busy && rows.length === 0 && (
          <div className="border border-border p-8 text-center" data-testid="approvals-empty">
            <Inbox className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
            <div className="text-sm font-semibold">You&rsquo;re all caught up</div>
            <p className="text-xs text-muted-foreground mt-1">No tasks are waiting on your action right now.</p>
          </div>
        )}
        {!busy && groups.map((g) => (
          <div key={g.emp.id || "_none_"} className="border border-border" data-testid={`approvals-group-${g.emp.id || "none"}`}>
            <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{g.emp.full_name || "Unknown"}</div>
                  {(g.emp.designation || g.emp.emp_id) && (
                    <div className="text-[10px] text-muted-foreground truncate">
                      {g.emp.designation}{g.emp.emp_id && ` · #${g.emp.emp_id}`}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {g.rows.length} task{g.rows.length === 1 ? "" : "s"}
              </div>
            </div>
            <ul>
              <li className="grid grid-cols-[200px_minmax(240px,1.6fr)_100px_120px_minmax(260px,1.4fr)_180px] gap-3 px-3 py-2 border-b border-border bg-muted/20 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                <div>Kind</div>
                <div>Task</div>
                <div>Due</div>
                <div>My role</div>
                <div>Progress</div>
                <div className="text-right">Action</div>
              </li>
              {g.rows.map((r) => (
                <ApprovalRow key={r.id} row={r} membersById={membersById} onChanged={load} />
              ))}
            </ul>
          </div>
        ))}
        </>)}
      </div>
    </div>
  );
}
